import { TDatabaseId } from '@infotech-soe/metaschema';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { LOCALES } from '../../../../constants/intl';
import { NamedDomain } from '../../../../infrastructure/schemas/storage/NamedDomain';
import { IFrontDatasetPreset } from '../../../../models/user-config/DisplayModeConfig';
import DatasetService from '../../../../services/dataset/DatasetService';
import { DatasetCurrentState } from '../../../../services/dataset/view-model/DatasetCurrentState';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../../../services/dataset/view-model/DatasetGridRow';
import UniqueId from '../../../../services/uid/UniqueId';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getActionProgressState } from '../../../../store/selectors/actionProgress';
import {
  getCategoryConditions,
  getCategoryState,
  isDatasetVisible,
} from '../../../../store/selectors/category';
import {
  filterDatasetsForCategory,
  getDatasetCategoryId,
  getDatasetRowIdField,
  getSecondaryTablesData,
} from '../../../../store/selectors/dataset';
import actionProgress from '../../../../store/slices/actionProgress';
import ExcelExportDialog, {
  ExcelExportOptions,
  MainTableMode,
  SecondaryMode,
} from '../components/ExcelExportDialog';
import downloadCSV from '../utils/downloadCSV';
import {
  downloadExcel,
  PerRowSecondaryData,
  SheetData,
  StackedSheetInput,
} from '../utils/downloadExcel';
import { TDatasetCellValue } from '../utils/getFormattedValue';
import { getOrderedColumns } from '../utils/getOrderedColumns';

const datasetService = new DatasetService();

const messages = defineMessages({
  title: {
    id: 'core.category.grid.excelExport.title',
    defaultMessage: 'Export to Excel',
  },
});

const buildTogetherSheet = (
  mainSheet: SheetData,
  secondarySheets: SheetData[]
): SheetData => {
  const headers = [
    ...mainSheet.headers,
    ...secondarySheets.flatMap(s => s.headers),
  ];

  const secColCount = secondarySheets.reduce(
    (acc, s) => acc + s.headers.length,
    0
  );

  const maxSecRows = Math.max(...secondarySheets.map(s => s.rows.length), 0);
  const emptySecRow = new Array<TDatasetCellValue>(secColCount).fill(null);
  const combinedSecRows: TDatasetCellValue[][] =
    maxSecRows === 0
      ? [emptySecRow]
      : Array.from({ length: maxSecRows }, (_, i) =>
          secondarySheets.flatMap(s =>
            i < s.rows.length
              ? s.rows[i]
              : new Array<TDatasetCellValue>(s.headers.length).fill(null)
          )
        );

  const rows = mainSheet.rows.flatMap(mainRow =>
    combinedSecRows.map(secRow => [...mainRow, ...secRow])
  );

  const columnFormats = [
    ...(mainSheet.columnFormats ??
      new Array(mainSheet.headers.length).fill(undefined)),
    ...secondarySheets.flatMap(
      s => s.columnFormats ?? new Array(s.headers.length).fill(undefined)
    ),
  ];

  return { name: mainSheet.name, headers, rows, columnFormats };
};

export interface UseDatasetExportParams {
  datasetId: UniqueId;
  orderedColumns: DatasetGridColumn[];
  activeRowIndex: number;
  selectedRows: DatasetGridRow[];
  allRows: DatasetGridRow[];
  rules: IFrontDatasetPreset['rules'];
  locale: LOCALES;
  datasetTitle: string;
  convert: (
    format: DatasetColumnFormat,
    value: TDatasetCellValue,
    domain: NamedDomain,
    useGrouping: boolean
  ) => TDatasetCellValue;
}

const useDatasetExport = ({
  datasetId,
  orderedColumns,
  activeRowIndex,
  selectedRows,
  allRows,
  rules,
  locale,
  datasetTitle,
  convert,
}: UseDatasetExportParams) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const cancelRequestedRef = useRef(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    document.body.style.cursor = isPending ? 'wait' : '';
    return () => {
      document.body.style.cursor = '';
    };
  }, [isPending]);

  const categoryId = useAppSelector(state =>
    getDatasetCategoryId(state, datasetId)
  );

  const secondaryTablesData = useAppSelector(state =>
    getSecondaryTablesData(state, datasetId)
  );

  const activeSecondaryId = useAppSelector(
    state =>
      secondaryTablesData.find(({ id }) => isDatasetVisible(state, id))?.id ??
      secondaryTablesData[0]?.id
  );

  const categoryState = useAppSelector(state =>
    getCategoryState(state, categoryId)
  );
  const datasetsState = useAppSelector(
    state =>
      filterDatasetsForCategory(state, categoryId) as Record<
        UniqueId,
        DatasetCurrentState
      >
  );
  const conditions = useAppSelector(state =>
    getCategoryConditions(state, categoryId)
  );
  const rowIdField = useAppSelector(state =>
    getDatasetRowIdField(state, datasetId)
  );
  const actionProgressState = useAppSelector(getActionProgressState);

  useEffect(() => {
    cancelRequestedRef.current = !!actionProgressState?.cancelling;
  }, [actionProgressState?.cancelling]);

  const buildSheetData = useCallback(
    (
      sheetRows: DatasetGridRow[],
      sheetColumns: DatasetGridColumn[],
      sheetRules: IFrontDatasetPreset['rules'],
      sheetTitle: string
    ): SheetData => {
      const visibleColumns = sheetColumns.filter(
        ({ field }) => sheetRules[field]?.visibility
      );
      const headers = visibleColumns.map(({ field, title }) => {
        return sheetRules[field]?.title?.[locale] || title;
      });
      const rows = sheetRows.map(row =>
        visibleColumns.map(({ field, formatter, format, domain }) => {
          if (
            format === DatasetColumnFormat.NUMBER &&
            domain?.subtype !== 'time'
          ) {
            return row[field] ?? null;
          }
          if (formatter) return formatter(row[field], { row });
          if (
            (format === DatasetColumnFormat.DATE ||
              format === DatasetColumnFormat.DATE_TIME ||
              format === DatasetColumnFormat.DATE_TIME_SEC) &&
            row[field] !== null &&
            row[field] !== undefined
          ) {
            const d = new Date(row[field] as string);
            return new Date(
              Date.UTC(
                d.getFullYear(),
                d.getMonth(),
                d.getDate(),
                d.getHours(),
                d.getMinutes(),
                d.getSeconds()
              )
            );
          }
          return convert(format, row[field], domain, false);
        })
      );
      const columnFormats = visibleColumns.map(
        ({ field, format, domain, formatter }) => {
          if (
            format === DatasetColumnFormat.NUMBER &&
            domain?.subtype !== 'time'
          ) {
            if (domain?.subtype === 'year') return '0';
            let precision = domain?.precision ?? 0;
            if (formatter) {
              const sampleRow = sheetRows.find(row => row[field] !== null);
              if (sampleRow) {
                const formatted = formatter(sampleRow[field], {
                  row: sampleRow,
                });
                const str = String(formatted)
                  .replace(/\s/g, '')
                  .replace(',', '.');
                const dotIndex = str.indexOf('.');
                precision = dotIndex >= 0 ? str.length - dotIndex - 1 : 0;
              }
            }
            return precision > 0 ? `#,##0.${'0'.repeat(precision)}` : '#,##0';
          }
          if (format === DatasetColumnFormat.DATE) return 'dd.mm.yyyy';
          if (format === DatasetColumnFormat.DATE_TIME)
            return 'dd.mm.yyyy hh:mm';
          if (format === DatasetColumnFormat.DATE_TIME_SEC)
            return 'dd.mm.yyyy hh:mm:ss';
          return undefined;
        }
      );
      return { name: sheetTitle, headers, rows, columnFormats };
    },
    [locale, convert]
  );

  const fetchSecondarySheets = useCallback(
    async (
      mainRow: DatasetGridRow,
      tablesData = secondaryTablesData
    ): Promise<SheetData[]> => {
      const modifiedState = {
        ...datasetsState,
        [datasetId]: {
          ...datasetsState[datasetId],
          activeRowId: mainRow[rowIdField] as TDatabaseId,
        },
      };
      const sheets: SheetData[] = [];

      for (const { id, columns, config, title } of tablesData) {
        if (cancelRequestedRef.current) return sheets;

        const { rows } = await datasetService.updateDataset(
          categoryState,
          modifiedState,
          id,
          conditions,
          undefined,
          datasetsState[id]?.config?.sort
        );
        const preset = config.preset[config.currentPreset];
        const orderedCols = getOrderedColumns(config.columnsOrder, columns);
        sheets.push(buildSheetData(rows, orderedCols, preset.rules, title));
      }

      return sheets;
    },
    [
      categoryState,
      datasetsState,
      conditions,
      rowIdField,
      datasetId,
      secondaryTablesData,
      buildSheetData,
    ]
  );

  const exportCSV = useCallback(() => {
    const rows = selectedRows.length > 0 ? selectedRows : allRows;
    const csv = [
      orderedColumns
        .map(({ field, title }) => {
          const renamedTitle = rules[field].title?.[locale];
          return renamedTitle || title;
        })
        .join(';'),
      ...rows.map(row =>
        orderedColumns
          .map(({ field, formatter, format, domain }) => {
            const unescapedString = formatter
              ? formatter(row[field], { row })
              : convert(format, row[field], domain, false);
            return format === DatasetColumnFormat.NUMBER
              ? unescapedString
              : `"${unescapedString?.replace(/"/gi, '""') ?? ''}"`;
          })
          .join(';')
      ),
    ].join('\n');
    downloadCSV(csv, `${datasetTitle}.csv`);
  }, [
    orderedColumns,
    selectedRows,
    allRows,
    rules,
    locale,
    convert,
    datasetTitle,
  ]);

  const handleExcelConfirm = useCallback(
    ({ mainTable, secondary }: ExcelExportOptions) => {
      setIsDialogOpen(false);
      cancelRequestedRef.current = false;

      dispatch(
        actionProgress.start({ actionName: formatMessage(messages.title) })
      );
      dispatch(actionProgress.update({ progress: 0, cancellable: true }));

      const activeRecord = allRows[activeRowIndex];
      const exportRows =
        mainTable === MainTableMode.ALL
          ? allRows
          : selectedRows.length > 0
            ? selectedRows
            : activeRecord
              ? [activeRecord]
              : [];

      const mainSheet = buildSheetData(
        exportRows,
        orderedColumns,
        rules,
        datasetTitle
      );

      setIsPending(true);
      void (async () => {
        try {
          if (secondary === SecondaryMode.NONE) {
            await downloadExcel([mainSheet], `${datasetTitle}.xlsx`);
            if (cancelRequestedRef.current) return;
            dispatch(actionProgress.update({ progress: 100 }));
            return;
          }

          const isActiveOnly =
            secondary === SecondaryMode.TOGETHER ||
            secondary === SecondaryMode.SEPARATE;
          const tablesToFetch = isActiveOnly
            ? secondaryTablesData.filter(({ id }) => id === activeSecondaryId)
            : secondaryTablesData;

          const secondarySheetsByRow: SheetData[][] = [];
          const totalRows = exportRows.length;
          for (let i = 0; i < totalRows && !cancelRequestedRef.current; i++) {
            const row = exportRows[i];
            secondarySheetsByRow.push(
              await fetchSecondarySheets(row, tablesToFetch)
            );
            const progress =
              totalRows > 0 ? Math.round(((i + 1) / totalRows) * 100) : 0;
            dispatch(
              actionProgress.update({
                progress,
                message: `${i + 1}/${totalRows}`,
                cancellable: true,
              })
            );
          }

          if (cancelRequestedRef.current) return;

          if (
            secondary === SecondaryMode.TOGETHER ||
            secondary === SecondaryMode.TOGETHER_ALL
          ) {
            const template = buildTogetherSheet(
              { ...mainSheet, rows: mainSheet.rows.slice(0, 1) },
              secondarySheetsByRow[0] ?? []
            );
            const rows = mainSheet.rows.flatMap(
              (mainRow, i) =>
                buildTogetherSheet(
                  { ...mainSheet, rows: [mainRow] },
                  secondarySheetsByRow[i] ?? []
                ).rows
            );
            await downloadExcel(
              [{ ...template, rows }],
              `${datasetTitle}.xlsx`
            );
          } else {
            const perRowData: PerRowSecondaryData[] = mainSheet.rows.map(
              (mainRow, i) => ({
                mainRow,
                secondarySheets: secondarySheetsByRow[i],
              })
            );
            const stackedSheet: StackedSheetInput = {
              name: mainSheet.name,
              headers: mainSheet.headers,
              columnFormats: mainSheet.columnFormats,
              perRowData,
            };
            await downloadExcel([stackedSheet], `${datasetTitle}.xlsx`);
          }

          dispatch(actionProgress.update({ progress: 100 }));
        } finally {
          setIsPending(false);
          dispatch(actionProgress.finish());
        }
      })();
    },
    [
      dispatch,
      formatMessage,
      allRows,
      activeRowIndex,
      selectedRows,
      orderedColumns,
      rules,
      datasetTitle,
      buildSheetData,
      fetchSecondarySheets,
      secondaryTablesData,
      activeSecondaryId,
    ]
  );

  const excelExportDialog = (
    <ExcelExportDialog
      open={isDialogOpen}
      hasSecondary={secondaryTablesData.length > 0}
      hasMultipleSecondary={secondaryTablesData.length > 1}
      onConfirm={handleExcelConfirm}
      onCancel={() => setIsDialogOpen(false)}
    />
  );

  return {
    exportCSV,
    exportExcel: () => setIsDialogOpen(true),
    excelExportDialog,
  };
};

export default useDatasetExport;
